import type { InstructorCourse } from "services/instructorCoursesService";
import type { InstructorStudent, InstructorStudentProfile } from "services/instructorStudentsService";

type SubjectLike = {
  name?: string | null;
  stage_id?: number | string | null;
  stage_ids?: number[];
  grade_ids?: number[];
  grade_id?: number | string | null;
  class_id?: number | string | null;
};

export type StudentAvailableCourse = {
  id: number;
  title: string;
  name?: string;
  pricing_type?: "free" | "paid" | string;
  is_free?: boolean;
  is_paid?: boolean;
  price?: number;
  currency?: string;
  subject?: SubjectLike | null;
  material?: SubjectLike | null;
  grade_id?: number | null;
  already_enrolled?: boolean;
  match_source?: "course" | "material" | "subject" | string | null;
};

function toId(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function normalizeStudentProfile(
  profile: InstructorStudentProfile | null | undefined
): InstructorStudentProfile | null {
  if (!profile) return null;

  return {
    ...profile,
    stage_id: toId(profile.stage_id) ?? toId(profile.stage?.id) ?? null,
    grade_id: toId(profile.grade_id) ?? toId(profile.grade?.id) ?? null,
  };
}

function subjectIds(subject: SubjectLike | null | undefined) {
  return {
    stage_ids: subject?.stage_ids?.length ? subject.stage_ids.map(Number) : [toId(subject?.stage_id)].filter((id): id is number => id !== null),
    grade_ids: subject?.grade_ids?.length ? subject.grade_ids.map(Number) : [toId(subject?.grade_id) ?? toId(subject?.class_id)].filter((id): id is number => id !== null),
  };
}

export function formatStudentSpecialty(
  profile: InstructorStudentProfile | null | undefined,
  isArabic: boolean
): string {
  const normalized = normalizeStudentProfile(profile);
  if (!normalized) return "—";

  if (normalized.stage?.name || normalized.grade?.name) {
    return [normalized.stage?.name, normalized.grade?.name].filter(Boolean).join(" · ") || "—";
  }

  return isArabic ? "—" : "—";
}

export function courseMatchesStudentProfile(
  course: Pick<InstructorCourse, "subject" | "grade_id"> | StudentAvailableCourse,
  profile: InstructorStudentProfile | null | undefined
): boolean {
  const normalized = normalizeStudentProfile(profile);
  if (!normalized) return false;
  const courseGradeId = toId(course.grade_id);
  if (normalized.grade_id && courseGradeId && normalized.grade_id !== courseGradeId) return false;

  const subject = subjectIds(
    ((course as InstructorCourse).subject ?? (course as StudentAvailableCourse).material) as
      | SubjectLike
      | null
      | undefined
  );

  if (normalized.stage_id && subject.stage_ids.length > 0 && !subject.stage_ids.includes(normalized.stage_id)) return false;
  if (normalized.grade_id && subject.grade_ids.length > 0 && !subject.grade_ids.includes(normalized.grade_id)) return false;
  return true;
}

export function filterCoursesForStudent<T extends { id: number; subject?: SubjectLike | null }>(
  courses: T[],
  student: Pick<InstructorStudent, "profile"> | null | undefined,
  excludeCourseIds: number[] = []
): T[] {
  const excluded = new Set(excludeCourseIds.map((id) => Number(id)));
  const profile = normalizeStudentProfile(student?.profile);

  return courses.filter(
    (course) => !excluded.has(Number(course.id)) && courseMatchesStudentProfile(course, profile)
  );
}

export function unwrapAvailableCoursesResult(result: unknown): StudentAvailableCourse[] {
  if (Array.isArray(result)) {
    return result as StudentAvailableCourse[];
  }
  if (result && typeof result === "object" && Array.isArray((result as { data?: unknown }).data)) {
    return (result as { data: StudentAvailableCourse[] }).data;
  }
  return [];
}
